/**
 * LiveActivityToast.tsx
 * Thin wrapper that triggers sonner toasts for live exam activity.
 * Uses the existing Toaster component (sonner) so notifications
 * match the app's existing toast design.
 *
 * Shows one activity per 10 seconds, cycles through active + completed sessions.
 * Uses professional styled icons instead of emoji.
 */

import { useEffect, useRef, createElement } from 'react';
import { toast } from 'sonner';
import { useLiveActivity } from '../hooks/useLiveActivity';
import { trackLiveToastImpression, trackLiveToastClick } from '../telemetry/googleAnalytics';
import { timeAgo } from '../lib/utils';

interface LiveActivityToastProps {
  enabled?: boolean;
  onExamClick?: (examId: string) => void;
}

/** Professional styled icon for completed exam */
function CompletedIcon() {
  return createElement(
    'div',
    {
      style: {
        width: 20,
        height: 20,
        borderRadius: '50%',
        background: 'linear-gradient(135deg, #10b981, #059669)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      },
    },
    createElement(
      'svg',
      {
        width: 12,
        height: 12,
        viewBox: '0 0 24 24',
        fill: 'none',
        stroke: 'white',
        strokeWidth: 3,
        strokeLinecap: 'round',
        strokeLinejoin: 'round',
      },
      createElement('polyline', { points: '20 6 9 17 4 12' })
    )
  );
}

/** Professional styled icon for active exam */
function ActiveIcon() {
  return createElement(
    'div',
    {
      style: {
        width: 20,
        height: 20,
        borderRadius: '50%',
        background: 'linear-gradient(135deg, #6366f1, #4f46e5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      },
    },
    createElement(
      'svg',
      {
        width: 12,
        height: 12,
        viewBox: '0 0 24 24',
        fill: 'none',
        stroke: 'white',
        strokeWidth: 2.5,
        strokeLinecap: 'round',
        strokeLinejoin: 'round',
      },
      createElement('path', { d: 'M12 20h9' }),
      createElement('path', { d: 'M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z' })
    )
  );
}

export function LiveActivityToast({ enabled = true, onExamClick }: LiveActivityToastProps) {
  const activity = useLiveActivity({ enabled, intervalMs: 60_000 });
  const shownRef = useRef<Set<string>>(new Set());
  const initialDoneRef = useRef(false);
  const timersRef = useRef<ReturnType<typeof setTimeout>[]>([]);

  const sessions = activity?.allSessions ?? [];

  /** Create a unique key for each session to track what's been shown */
  const sessionKey = (s: {
    maskedEmail: string;
    examId: string;
    type: string;
    startedAt?: string;
    completedAt?: string;
  }) => `${s.maskedEmail}::${s.examId}::${s.type}::${s.startedAt || s.completedAt || ''}`;

  /** Show a single toast for a session */
  const showToast = (session: (typeof sessions)[0]) => {
    const key = sessionKey(session);
    if (shownRef.current.has(key)) return;
    shownRef.current.add(key);

    const isActive = session.type === 'ACTIVE';
    const time = isActive
      ? session.startedAt
        ? timeAgo(session.startedAt)
        : ''
      : session.completedAt
        ? timeAgo(session.completedAt)
        : '';

    let description: string;
    if (isActive) {
      description = `đang làm ${session.examTitle}`;
      if (time) description += ` · ${time}`;
    } else {
      description = `hoàn thành ${session.examTitle}`;
      if (session.score != null && session.totalScore) {
        description += ` · ${session.score}/${session.totalScore} điểm`;
      }
      if (time) description += ` · ${time}`;
    }

    toast(session.maskedEmail, {
      description,
      action: onExamClick
        ? {
            label: 'Xem đề',
            onClick: () => {
              trackLiveToastClick(session.examId, session.examTitle);
              onExamClick(session.examId);
            },
          }
        : undefined,
      duration: 7_500,
      icon: isActive ? createElement(ActiveIcon) : createElement(CompletedIcon),
    });

    trackLiveToastImpression(session.examId, session.examTitle, session.type);
  };

  useEffect(() => {
    if (!sessions.length || !enabled) return;

    if (!initialDoneRef.current) {
      // First load: show max 3 toasts staggered 8s apart, mark all existing as "seen"
      initialDoneRef.current = true;

      // Mark ALL current sessions as seen (so we don't re-toast them later)
      sessions.forEach(s => shownRef.current.add(sessionKey(s)));

      // Show only the first 3 (staggered)
      const initialBatch = sessions.slice(0, 3);
      // Clear the keys for these 3 so showToast() can fire them
      initialBatch.forEach(s => shownRef.current.delete(sessionKey(s)));

      initialBatch.forEach((session, i) => {
        const timer = setTimeout(() => showToast(session), 3_000 + i * 8_000);
        timersRef.current.push(timer);
      });
    } else {
      // Subsequent polls: only toast genuinely new sessions
      const newSessions = sessions.filter(s => !shownRef.current.has(sessionKey(s)));
      newSessions.slice(0, 3).forEach((session, i) => {
        const timer = setTimeout(() => showToast(session), i * 8_000);
        timersRef.current.push(timer);
      });
    }

    return () => {
      timersRef.current.forEach(clearTimeout);
      timersRef.current = [];
    };
  }, [sessions.length, enabled]); // eslint-disable-line react-hooks/exhaustive-deps

  return null;
}
